import { ReactNode, useMemo, useState } from "react";

import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  SxProps,
  CardHeader,
  IconButton,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { formatDateStr } from "../../../../features/data/time";
import { ImportJobStatus } from "../../../../features/import/import";
import { IconTypes } from "../../../../components/icons.types";

export const ImportProgress = ({
  jobStatus,
  onClose,
  fields = ["code", "index"],
  onStop,
  sx,
}: {
  jobStatus: ImportJobStatus;
  fields?: ("code" | "index")[];
  onClose?: () => void;
  onStop?: () => void;
  sx?: SxProps;
}) => {
  const importProgress = useMemo(() => {
    if (!jobStatus?.counters) return 0;

    // Calculate total progress across all tracked items
    const allCounters = Object.keys(jobStatus.counters);
    const totalCounters = allCounters.filter((key) => key.endsWith("-total"));

    let totalItems = 0;
    let doneItems = 0;

    totalCounters.forEach((totalKey) => {
      const baseKey = totalKey.replace("-total", "");
      const total = jobStatus.counters[totalKey] || 0;
      const done = jobStatus.counters[baseKey] || 0;
      totalItems += total;
      doneItems += done;
    });

    return totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;
  }, [jobStatus]);

  if (!jobStatus) return null;

  return (
    <Card sx={{ mt: 2, ...sx }} elevation={1}>
      <CardHeader
        title={jobStatus.job?.finished ? "Finished" : "Importing"}
        action={
          <IconButton onClick={onClose} disabled={!jobStatus.job?.finished}>
            <IconTypes.Close />
          </IconButton>
        }
      />
      <CardContent>
        <Typography variant="caption" my={1} sx={{ display: "flex", gap: 4 }}>
          {!jobStatus.job?.finished && (
            <>
              {(() => {
                if (!jobStatus.counters) return null;

                // Get all counter types that have totals
                const totalCounters = Object.keys(jobStatus.counters)
                  .filter(
                    (key) =>
                      key.endsWith("-total") && jobStatus.counters![key] > 0
                  )
                  .map((totalKey) => {
                    const baseKey = totalKey.replace("-total", "");
                    return {
                      type: baseKey,
                      current: jobStatus.counters![baseKey] || 0,
                      total: jobStatus.counters![totalKey] || 0,
                      label: baseKey === "error-count" ? "errors" : baseKey,
                    };
                  });

                return totalCounters.map(({ type, current, total, label }) => (
                  <span key={type}>
                    {current} / {total} {label}
                  </span>
                ));
              })()}
              {jobStatus.counters?.["error-count"] > 0 && (
                <span>{jobStatus.counters?.["error-count"] || 0} errors</span>
              )}
            </>
          )}
        </Typography>
        <LinearProgress
          variant="determinate"
          color={importProgress < 100 ? "primary" : "success"}
          value={importProgress}
        />
        <Typography
          component="div"
          variant="caption"
          sx={{ mt: 1, minHeight: 30 }}
        >
          {jobStatus.duration} {jobStatus.status}
        </Typography>

        {onStop && !jobStatus.job?.finished && (
          <Button
            onClick={() => onStop()}
            color="error"
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Stop Import
          </Button>
        )}

        {jobStatus.errors?.length > 0 && (
          <>
            <Typography variant="h6" my={1}>
              Import Errors
            </Typography>
            <TableContainer sx={{ maxHeight: 250, mt: 1 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {fields.map((f) => (
                      <TableCell key={f}>{f}</TableCell>
                    ))}
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobStatus.errors.map((e, i) => (
                    <TableRow key={i}>
                      {fields.map((f) => (
                        <TableCell key={f}>{e[f]}</TableCell>
                      ))}
                      <TableCell
                        sx={{
                          color: (theme) =>
                            e.isWarning ? null : theme.palette.error.main,
                        }}
                      >
                        {e.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const ImportStatusLine = ({
  jobStatus,
  actions,
  sx,
}: {
  jobStatus?: ImportJobStatus;
  /** Shown on the right, after the issues button (e.g. PDF import's "Open catalog"). */
  actions?: ReactNode;
  sx?: SxProps;
}) => {
  const [errorsOpen, setErrorsOpen] = useState(false);

  if (!jobStatus?.job) return null;

  const issueCount =
    (jobStatus.errorCount ?? 0) + (jobStatus.warningCount ?? 0);
  const hasIssueDetails = (jobStatus.errors?.length ?? 0) > 0;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 1.5,
          rowGap: 0.5,
          py: 0.75,
          px: 1.25,
          borderRadius: 1,
          bgcolor: "action.hover",
          ...sx,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Last import: {formatDateStr(jobStatus.updated, true)}
        </Typography>
        {jobStatus.job.name && (
          <Typography variant="caption" color="text.secondary">
            · {jobStatus.job.name}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{ flex: "1 1 auto", minWidth: 0, wordBreak: "break-word" }}
        >
          {jobStatus.status}
        </Typography>
        {issueCount > 0 && hasIssueDetails && (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={() => setErrorsOpen(true)}
            sx={{ ml: "auto", py: 0.25, minHeight: 0 }}
          >
            View {issueCount} {issueCount === 1 ? "issue" : "issues"}
          </Button>
        )}
        {actions && (
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>{actions}</Box>
        )}
      </Box>

      <Dialog
        open={errorsOpen}
        onClose={() => setErrorsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Import errors and warnings
          {(jobStatus.errorCount ?? 0) > 0 && (
            <Chip
              size="small"
              label={`${jobStatus.errorCount} error${jobStatus.errorCount === 1 ? "" : "s"}`}
              color="error"
            />
          )}
          {(jobStatus.warningCount ?? 0) > 0 && (
            <Chip
              size="small"
              label={`${jobStatus.warningCount} warning${jobStatus.warningCount === 1 ? "" : "s"}`}
              color="warning"
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>Type</TableCell>
                  <TableCell sx={{ width: 120 }}>Code</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobStatus.errors?.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell
                      sx={{
                        color: (theme) =>
                          e.isWarning
                            ? theme.palette.warning.main
                            : theme.palette.error.main,
                        fontWeight: 500,
                      }}
                    >
                      {e.isWarning ? "Warning" : "Error"}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {e.code ?? "—"}
                    </TableCell>
                    <TableCell sx={{ wordBreak: "break-word" }}>
                      {e.message}
                      {e.field && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          ({e.field}
                          {e.value != null && `: ${e.value}`})
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
